import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { withStyles } from "@material-ui/core/styles";
import { Formik } from "formik";
import { TextField } from "@material-ui/core";
import { getCountries } from "../../../../utils/parseProvinceAbbr";
import Checkbox from "../../../../components/Form/Checkbox";
import appConfig from "../../../../../app-config";

const Wrapper = styled.div`
  padding: 8px;
  max-width: 100%;

  @media (min-width: 786px) {
    width: 100%;
  }
`;

const AddressLabel = styled.label`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
`;

const EnterAddressInput = styled.input`
  width: 100%;
  border: none;
  font-size: 16px;
  border-bottom: 1px solid ${(props) => props.theme.darkGray};
  margin-bottom: 23px;

  &:focus {
    outline: none;
  }
`;

const FormWrapper = styled.div`
  position: relative;
`;

const DeliveryOptionsWrapper = styled.div`
  display: grid;
  grid-gap: 23px;
`;

const styles = (theme) => ({
  input: {
    fontFamily: theme.typography.fontFamily.main,
  },
});

const STREET_NUMBER = "street_number";
const ROUTE = "route";
const LOCALITY = "locality";
const ADMINISTRATIVE_ADREA_LEVEL_1 = "administrative_area_level_1";
const COUNTRY = "country";
const POSTAL_CODE = "postal_code";
const POSTAL_TOWN = "postal_town";

const COUNTRIES = getCountries();

const DeliveryAddressesForm = ({
  guestDeliveryFormInitialAddress,
  setGuestDeliveryAddressFormInitialValues,
  updateInterDeliveryDetails,
  getMarketplaceDeliveryLocations,
  isMarketplaceDelivery,
  classes,
  setDeliveryLocation,
}) => {
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [line2, setLine2] = useState(null);
  const [details, setDetails] = useState(null);

  function isNumeric(str) {
    if (typeof str != "string") return false;
    return !isNaN(str) && !isNaN(parseFloat(str));
  }

  useEffect(() => {
    if (selectedAddress) {
      setGuestDeliveryAddressFormInitialValues({
        ...selectedAddress,
        line2,
        details,
      });
    }
  }, [line2, details]);

  useEffect(() => {
    let input = document.getElementById("pac-input");

    if (guestDeliveryFormInitialAddress) {
      input.value = `${guestDeliveryFormInitialAddress.line1}, ${guestDeliveryFormInitialAddress.city}, ${guestDeliveryFormInitialAddress.state}, ${guestDeliveryFormInitialAddress.zip}, ${guestDeliveryFormInitialAddress.country}`;

      setSelectedAddress({
        line1: guestDeliveryFormInitialAddress.line1,
        city: guestDeliveryFormInitialAddress.city,
        state: guestDeliveryFormInitialAddress.state,
        zip: guestDeliveryFormInitialAddress.zip,
        country: guestDeliveryFormInitialAddress.country,
      });
    }

    let searchBox = new window.google.maps.places.Autocomplete(input, {
      componentRestrictions: {
        country: appConfig.countriesSupported
          ? appConfig.countriesSupported
          : "ca",
      },
    });
    // map.controls[google.maps.ControlPosition.TOP_LEFT].push(input);
    // geocoder = new window.google.maps.Geocoder();

    let searchBoxListener = searchBox.addListener("place_changed", () => {
      let place = searchBox.getPlace();

      if (!place) {
        setSelectedAddress(null);
        return;
      }

      const addressComponents = place.address_components;
      let streetNumber;
      let route;
      let locality;
      let administrativeAreaLevel1;
      let country;
      let postalCode;

      try {
        streetNumber = addressComponents.find(
          (component) =>
            component.types.includes(STREET_NUMBER) ||
            component.types.includes(ROUTE)
        );

        route = addressComponents.find((component) =>
          component.types.includes(ROUTE)
        );

        locality = addressComponents.find(
          (component) =>
            component.types.includes(LOCALITY) ||
            component.types.includes(POSTAL_TOWN)
        );

        administrativeAreaLevel1 = addressComponents.find((component) =>
          component.types.includes(ADMINISTRATIVE_ADREA_LEVEL_1)
        );

        postalCode = addressComponents.find((component) =>
          component.types.includes(POSTAL_CODE)
        );

        country = COUNTRIES.find(
          (c) =>
            c.name ===
            addressComponents.find((component) =>
              component.types.includes(COUNTRY)
            ).long_name
        ).abbreviation;
      } catch (e) {
        document.getElementById("pac-input").value = "";
      }
      const fullAddress = document.getElementById("pac-input").value;

      streetNumber = streetNumber
        ? streetNumber.long_name
        : isNumeric(fullAddress.split(" ")[0])
        ? fullAddress.split(" ")[0]
        : null;

      route = route ? route.long_name : null;
      locality = locality ? locality.long_name : null;
      postalCode = postalCode ? postalCode.long_name : null;
      administrativeAreaLevel1 = administrativeAreaLevel1
        ? administrativeAreaLevel1.long_name
        : null;

      const delivery_address = {
        line1:
          route !== streetNumber
            ? `${streetNumber} ${route}`
            : `${fullAddress.split(" ")[0]} ${route}`,
        city: locality,
        state: administrativeAreaLevel1,
        zip: postalCode,
        country: country,
        is_preferred: false,
        type: "home",
      };

      setGuestDeliveryAddressFormInitialValues(delivery_address);
      updateInterDeliveryDetails(delivery_address);
      setSelectedAddress(delivery_address);

      if (isMarketplaceDelivery) {
        setDeliveryLocation(delivery_address);
      }
    });

    return () => {
      window.google.maps.event.removeListener(searchBoxListener);
    };
  }, []);

  return (
    <Wrapper>
      <Formik
        initialValues={
          guestDeliveryFormInitialAddress
            ? {
                line2: guestDeliveryFormInitialAddress.line2,
                details: guestDeliveryFormInitialAddress.details,
                is_preferred: false,
                type: "home",
              }
            : {
                line2: "",
                details: "",
                is_preferred: false,
                type: "home",
              }
        }
      >
        {({ values, setFieldValue, handleChange, handleBlur }) => (
          <FormWrapper>
            {selectedAddress && <AddressLabel> Address</AddressLabel>}
            <EnterAddressInput
              id="pac-input"
              class="controls"
              type="text"
              placeholder="Enter Address"
              onChange={() => {
                setGuestDeliveryAddressFormInitialValues(null);
                setSelectedAddress(null);
              }}
            />
            <DeliveryOptionsWrapper>
              <TextField
                id="DeliveryAddresses-AddressLine2"
                label="Apt/ Suite/ Floor"
                name="line2"
                type="text"
                value={line2}
                onBlur={handleBlur}
                onChange={(ev) => setLine2(ev.target.value)}
                InputLabelProps={{
                  className: classes.input,
                }}
                style={{ margin: 0 }}
              />
              <TextField
                id="DeliveryAddresses-DeliveryNote"
                label="Delivery note"
                name="details"
                type="text"
                value={details}
                onBlur={handleBlur}
                onChange={(ev) => setDetails(ev.target.value)}
                InputLabelProps={{
                  className: classes.input,
                }}
              />
            </DeliveryOptionsWrapper>
          </FormWrapper>
        )}
      </Formik>
    </Wrapper>
  );
};

export default withStyles(styles)(DeliveryAddressesForm);
